import React, { useEffect, useState, useRef, Fragment } from 'react';
import Lottie from "lottie-react";
import loaderAnimation from "../assets/loader.json";
import mailSent from "../assets/mail-sent.json";
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import StatusAccordion from './StatusAccordion';

import * as XLSX from 'xlsx';


import { Dialog, Transition } from '@headlessui/react';
import { FaUser, FaClock } from 'react-icons/fa';
import {
  Mail,
  UserPlus,
  PenSquare,
  Upload,
  CheckCircle2,
  AlertTriangle,
  Inbox,
  FileText,
  Eye,
  X,
  Search,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import axios from 'axios';
import { useGlobalContext } from '../pages/AUTH/GlobalContext';

const EmailSender = () => {
  console.log("EMAIL SENDER COMPONENT RENDERED");

  const { user } = useGlobalContext();
  const token = localStorage.getItem("jwtToken");
  // Kept in sync with navbar.js's showAdminLink and the backend's
  // deleteRecruiter check — same accounts count as admin everywhere.
  const isAdmin = user?.role === 'admin' || user?.email?.toLowerCase() === 'rohitshekrsingh@gmail.com';
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recruiters, setRecruiters] = useState([]);
  const [recruiterSearch, setRecruiterSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(50);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [syncingSheet, setSyncingSheet] = useState(false);
  const loadMoreRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [newRecruiter, setNewRecruiter] = useState({ name: "", email: "", company: "" });
  const [resumeFile, setResumeFile] = useState(null);
  const [emailLogs, setEmailLogs] = useState({});
  // Removed introduction and experience state

  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpTemplates, setFollowUpTemplates] = useState([]);
  const [selectedFollowUpTemplate, setSelectedFollowUpTemplate] = useState(null);


  const navigate = useNavigate();
  // Which recruiter the follow-up modal is acting on.
  const [followUpTargetId, setFollowUpTargetId] = useState(null);
  // Which recruiter's email journey is open in the "View Journey" modal.
  const [journeyRecruiter, setJourneyRecruiter] = useState(null);


  // After the Gmail OAuth consent redirect, the backend sends the browser
  // back here with ?gmail=connected|error|denied.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmailStatus = params.get('gmail');
    if (!gmailStatus) return;

    if (gmailStatus === 'connected') {
      toast.success('Gmail connected successfully!');
      setIsConnected(true);
    } else if (gmailStatus === 'denied') {
      toast.error('Gmail connection was cancelled.');
    } else {
      toast.error('Failed to connect Gmail. Please try again.');
    }

    params.delete('gmail');
    const query = params.toString();
    navigate(`/email-sender${query ? `?${query}` : ''}`, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const checkConnectionAndFetchRecruiters = async () => {
      try {
        // Pull in anything new from the linked Google Sheet before loading
        // the recruiter list, so the list reflects it on first paint —
        // this is what keeps the directory "synced" with the sheet.
        if (user?.email) {
          try {
            await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/recruiters/sync-sheet`, {
              userEmail: user.email,
            });
          } catch (syncError) {
            console.error('Sheet sync on load failed:', syncError);
          }
        }

        // None of these three depend on each other's result, so run them
        // concurrently instead of one-after-another.
        const [connectResult, recruiterResult, allLogsResult] = await Promise.allSettled([
          user?.email
            ? axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/check-connection`, {
                email: user.email,
              })
            : Promise.resolve(null),
          axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/recruiters`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { userEmail: user?.email }
          }),
          // Recruiters are a shared directory now, so this list can be
          // large — fetch this user's outreach history in a single bulk
          // call instead of firing one request per recruiter (that used to
          // open hundreds of parallel XHRs and hang the page once the
          // directory grew).
          user?.email
            ? axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/logs/all`, {
                params: { userEmail: user.email },
                headers: { Authorization: `Bearer ${token}` },
              })
            : Promise.resolve(null),
        ]);

        if (connectResult.status === 'fulfilled' && connectResult.value) {
          setIsConnected(connectResult.value.data?.success);
        }

        const recruitersList =
          recruiterResult.status === 'fulfilled'
            ? recruiterResult.value.data?.recruiters || []
            : [];

        const logsMap = {};
        const allLogs =
          allLogsResult.status === 'fulfilled' && allLogsResult.value
            ? allLogsResult.value.data?.logs || []
            : [];

        const logsByRecruiter = {};
        allLogs.forEach((log) => {
          const rid = log.recruiterId;
          if (!logsByRecruiter[rid]) logsByRecruiter[rid] = [];
          logsByRecruiter[rid].push(log);
        });

        recruitersList.forEach((rec) => {
          const logs = logsByRecruiter[rec._id] || [];
          logsMap[rec._id] = logs;

          const sentLogs = logs.filter((log) => log.status === 'thread_start');
          const followUpLogs = logs.filter((log) => log.status === 'follow_up');
          const latestFirst = [...sentLogs, ...followUpLogs].sort(
            (a, b) => new Date(b.createdAt || b.sentAt) - new Date(a.createdAt || a.sentAt)
          );

          if (latestFirst.length > 0) {
            const latestLog = latestFirst[0];
            rec.status = latestLog.status === 'follow_up' ? 'Follow-up Sent' : 'thread_start';
            rec.sentAt = latestLog.sentAt || latestLog.createdAt;
          } else {
            rec.status = 'No Response';
            rec.sentAt = null;
          }
        });

        setEmailLogs(logsMap);
        setRecruiters(recruitersList);
      } catch (error) {
        console.error('Error in connection check or fetching recruiters:', error);
      } finally {
        setLoading(false);
      }
    };

    checkConnectionAndFetchRecruiters();
  }, [user?.email, token]);

  const handleConnectClick = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/authUrl`);
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error fetching auth URL:', error);
    }
  };

  const fetchEmailLogs = async (recruiterId) => {
    try {
      // Check if we have the required data
      if (!user?.email) {
        console.error("User email not available");
        return;
      }

      if (!token) {
        console.error("Token not available");
        return;
      }

      // Find the recruiter to get their email and threadId
      const recruiter = recruiters.find(r => r._id === recruiterId);
      if (!recruiter) {
        console.error("Recruiter not found");
        return;
      }

      // Get existing email logs for this recruiter
      const existingLogs = emailLogs[recruiterId] || [];
      console.log("Existing logs for recruiter:", existingLogs);

      // Find threadId from existing logs
      const threadStartLog = existingLogs.find(log => log.status === 'thread_start');
      const threadId = threadStartLog?.threadId || threadStartLog?.messageId || recruiter.threadId || recruiter._id;

      // Debug logging
      console.log("User object:", user);
      console.log("User email:", user?.email);
      console.log("Recruiter object:", recruiter);
      console.log("Thread start log:", threadStartLog);
      console.log("ThreadId from logs:", threadId);
      console.log("Token:", token);

      const requestData = {
        email: user.email || "rohitbwb@gmail.com", // Fallback for testing
        threadId: threadId
      };

      console.log("Final request data:", requestData);

     const { data } = await axios.get(
  `${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/journey`,
  {
    params: {
      email: user.email,
      threadId: threadId
    },
    headers: {
      Authorization: `Bearer ${token}`
    }
  }
);


      console.log("API Response:", data);
      
      if (data.success && data.logs) {
        console.log("Setting logs for recruiter:", recruiterId, data.logs);
        setEmailLogs((prevLogs) => ({
          ...prevLogs,
          [recruiterId]: data.logs,
        }));
      } else {
        console.log("No logs found or API call failed");
        setEmailLogs((prevLogs) => ({
          ...prevLogs,
          [recruiterId]: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching email logs:", error);
    }
  };

  // Adding a recruiter whose email already exists updates that row
  // server-side (see createRecruiter) — reflect that as a replace, not an
  // append, so the table doesn't show a duplicate.
  const upsertRecruiterInState = (recruiter) => {
    setRecruiters(prev => {
      const exists = prev.some(r => r._id === recruiter._id);
      // Newest first, same ordering the backend already returns on load.
      return exists
        ? prev.map(r => (r._id === recruiter._id ? recruiter : r))
        : [recruiter, ...prev];
    });
  };

  const handleAddRecruiter = async () => {
    if (!newRecruiter.name || !newRecruiter.email) return;

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/recruiters`,
        {
          name: newRecruiter.name,
          email: newRecruiter.email,
          company: newRecruiter.company,
          userEmail: user?.email
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      upsertRecruiterInState(data.data);
      setIsModalOpen(false);
      setNewRecruiter({ name: "", email: "", company: "" });
    } catch (error) {
      console.error('Error adding recruiter:', error);
      toast.error('Failed to add recruiter.');
    }
  };

  const handleExcelUpload = async (e) => {

    console.log("checking on uopload");
    
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(worksheet); // [{ name: '', email: '', company: '' }, ...]

      for (const row of jsonData) {
        const name = row.Name || row.name;
        const email = row.Email || row.email;
        const company = row.Company || row.company || "";

        if (!name || !email) continue;

        try {
          const { data } = await axios.post(
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/recruiters`,
            {
              name,
              email,
              company,
              userEmail: user?.email
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          upsertRecruiterInState(data.data);
        } catch (error) {
          console.error(`Failed to add ${email}:`, error);
        }
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleDeleteRecruiter = async (recruiter) => {
    if (!isAdmin) return;
    if (!window.confirm(`Delete ${recruiter.name} (${recruiter.email}) from the shared directory? This can't be undone.`)) {
      return;
    }

    try {
      const { data } = await axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/recruiters/${recruiter._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!data.success) {
        toast.error(data.message || 'Failed to delete recruiter.');
        return;
      }

      setRecruiters((prev) => prev.filter((r) => r._id !== recruiter._id));
      setSelectedIds((prev) => {
        if (!prev.has(recruiter._id)) return prev;
        const next = new Set(prev);
        next.delete(recruiter._id);
        return next;
      });
      toast.success('Recruiter deleted.');
    } catch (error) {
      console.error('Error deleting recruiter:', error);
      toast.error(error.response?.data?.message || 'Failed to delete recruiter.');
    }
  };

  // Shared by "Delete Selected" and "Delete All" — both just differ in
  // which recruiters they target and what the confirm prompt says.
  const bulkDeleteRecruiters = async (targets, confirmMessage) => {
    if (!isAdmin || targets.length === 0) return;
    if (!window.confirm(confirmMessage)) return;

    try {
      const ids = targets.map((r) => r._id);
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/recruiters/bulk-delete`,
        { ids },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!data.success) {
        toast.error(data.message || 'Failed to delete recruiters.');
        return;
      }

      const idsSet = new Set(ids);
      setRecruiters((prev) => prev.filter((r) => !idsSet.has(r._id)));
      setSelectedIds(new Set());
      toast.success(`Deleted ${data.deletedCount ?? ids.length} recruiter${(data.deletedCount ?? ids.length) === 1 ? '' : 's'}.`);
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error(error.response?.data?.message || 'Failed to delete recruiters.');
    }
  };

  const handleDeleteSelected = () => {
    const targets = visibleRecruiters.filter((r) => selectedIds.has(r._id));
    bulkDeleteRecruiters(
      targets,
      `Delete ${targets.length} selected recruiter${targets.length === 1 ? '' : 's'} from the shared directory? This can't be undone.`
    );
  };

  const handleDeleteAllFiltered = () => {
    if (filteredRecruiters.length === 0) return;

    if (!hasActiveFilters) {
      // No search/status/date filter applied — this would wipe the whole
      // shared directory, not just what's currently on screen. Make that
      // unmistakable before even asking the normal confirm.
      const typed = window.prompt(
        `No filters are active — this would delete ALL ${filteredRecruiters.length} recruiters shared by every user. Type DELETE ALL to confirm.`
      );
      if (typed !== 'DELETE ALL') return;
      bulkDeleteRecruiters(filteredRecruiters, `Really delete all ${filteredRecruiters.length} recruiters? This can't be undone.`);
      return;
    }

    bulkDeleteRecruiters(
      filteredRecruiters,
      `Delete all ${filteredRecruiters.length} recruiters matching your current filters? This can't be undone.`
    );
  };

  const handleSyncSheet = async () => {
    setSyncingSheet(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/recruiters/sync-sheet`, {
        userEmail: user?.email,
      });

      if (!data.success) {
        toast.error(data.message || 'Failed to sync from Google Sheet.');
        return;
      }

      const { data: recruiterData } = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/recruiters`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userEmail: user?.email }
      });
      setRecruiters(recruiterData?.recruiters || []);

      const skippedNote = data.skipped ? ` (${data.skipped} skipped — missing/invalid email)` : '';
      if (data.added || data.updated) {
        toast.success(`Synced: ${data.added} added, ${data.updated} updated.${skippedNote}`);
      } else if (data.skipped) {
        toast.error(`Nothing synced — ${data.skipped} row${data.skipped === 1 ? '' : 's'} skipped (missing/invalid email).`);
      } else {
        toast.success('Already up to date with the sheet.');
      }
    } catch (error) {
      console.error('Manual sheet sync failed:', error);
      toast.error(error.response?.data?.message || 'Failed to sync from Google Sheet.');
    } finally {
      setSyncingSheet(false);
    }
  };

  const fetchFollowUpTemplates = async (recruiterId) => {
    try {
      const userEmail = user?.email;
      if (!userEmail) return;

      setFollowUpTargetId(recruiterId);

      console.log('Fetching templates for email:', userEmail);
      
const { data } = await axios.get(
  `${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/followup-templates/${userEmail}`,
  {
    headers: { Authorization: `Bearer ${token}` }
  }
);

      console.log('Follow-up templates response:', data);
      console.log('Data structure:', JSON.stringify(data, null, 2));
      
      // Try different possible property names
      const rawTemplates = data.followupTemplates || data.templates || data.followUpTemplates || data.follow_up_templates || [];
      console.log('Extracted templates:', rawTemplates);
      
      // Convert string templates to object format
      const templates = Array.isArray(rawTemplates) ? rawTemplates.map((template, index) => {
        if (typeof template === 'string') {
          return {
            id: index + 1,
            name: `Template ${index + 1}`,
            message: template
          };
        }
        return template; // If it's already an object, keep it as is
      }) : [];
      
      console.log('Processed templates:', templates);
      
      setFollowUpTemplates(templates);
      setShowFollowUpModal(true);
    } catch (error) {
      console.error('Error fetching follow-up templates:', error);
      setFollowUpTemplates([]);
      setShowFollowUpModal(true);
    }
  };


// Core "first touch" send, reused by both the single Send Mail button and
// bulk send. Throws on failure instead of alerting, so callers decide how
// to surface it (a toast for one recruiter, a tally for a batch).
const sendInitialEmail = async (recruiter) => {
  const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/template`, {
    params: { email: user?.email },
  });

  if (!response.data?.success || !response.data.htmlEmailTemplate?.finalHtml) {
    throw new Error('Failed to fetch email template. Please create a template first.');
  }

  let finalHtml = response.data.htmlEmailTemplate.finalHtml;
  const emailSubject = response.data.htmlEmailTemplate.subject || "Application For Engineering Roles (SDE-1)";

  finalHtml = finalHtml.replace(/{{\s*recruiter\.name\s*}}/g, recruiter.name);
  finalHtml = finalHtml.replace(/{{\s*name\s*}}/g, user?.username || user?.name || 'User');

  const formData = new FormData();
  formData.append("from", user?.email);
  formData.append("to", recruiter.email);
  formData.append("recruiterId", recruiter._id);
  formData.append("subject", emailSubject);
  formData.append("body", finalHtml);
  formData.append("attachment", resumeFile);

  const sendResponse = await axios.post(
    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/send`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  if (!sendResponse.data.success) {
    throw new Error(sendResponse.data.message || 'Failed to send email');
  }

  setRecruiters(prev =>
    prev.map(r =>
      r._id === recruiter._id ? { ...r, status: "thread_start", sentAt: new Date().toISOString() } : r
    )
  );
};

const describeSendError = (error) => {
  if (error.response?.data?.requiresReauth) {
    return 'Your Gmail connection has expired. Please reconnect your Gmail account.';
  }
  if (error.response?.data?.requiresAuth) {
    return 'Please connect your Gmail account first before sending emails.';
  }
  return error.response?.data?.message || error.message || 'Failed to send email';
};

const handleSendMail = async (recruiter) => {
  if (!resumeFile) {
    toast.error("Please upload your resume before sending the email.");
    return;
  }

  setSendingEmail(true);
  try {
    await sendInitialEmail(recruiter);
    toast.success("Email sent successfully!");
  } catch (error) {
    console.error('Error sending email:', error);
    toast.error(describeSendError(error));
  } finally {
    setTimeout(() => setSendingEmail(false), 1500);
  }
};

// Sends the same first-touch email to every selected recruiter, one at a
// time with a short pause between each — firing them all at once would
// look like a spam burst to Gmail (and to the recipients).
const handleBulkSend = async () => {
  if (!resumeFile) {
    toast.error("Please upload your resume before sending in bulk.");
    return;
  }

  // Admins can select rows of any status (for bulk-delete), but this is a
  // first-touch send — only actually send to the ones nobody's emailed yet.
  const selected = visibleRecruiters.filter((r) => selectedIds.has(r._id));
  const targets = selected.filter((r) => getDisplayStatus(r) === 'No Response');
  const skippedAlreadyContacted = selected.length - targets.length;
  if (targets.length === 0) {
    if (skippedAlreadyContacted > 0) {
      toast.error('All selected recruiters have already been contacted — nothing to send.');
    }
    return;
  }

  const skippedNote = skippedAlreadyContacted > 0
    ? ` (${skippedAlreadyContacted} already-contacted selection${skippedAlreadyContacted === 1 ? '' : 's'} will be skipped)`
    : '';
  if (!window.confirm(`Send your template to ${targets.length} recruiter${targets.length === 1 ? '' : 's'}?${skippedNote}`)) {
    return;
  }

  setBulkSending(true);
  setBulkProgress({ done: 0, total: targets.length });

  let successCount = 0;
  let failCount = 0;

  for (const recruiter of targets) {
    try {
      await sendInitialEmail(recruiter);
      successCount++;
    } catch (error) {
      console.error(`Bulk send failed for ${recruiter.email}:`, error);
      failCount++;
    }
    setBulkProgress((p) => ({ ...p, done: p.done + 1 }));
    await new Promise((resolve) => setTimeout(resolve, 800));
  }

  setBulkSending(false);
  setSelectedIds(new Set());
  if (failCount === 0) {
    toast.success(`Sent to all ${successCount} recruiters!`);
  } else {
    toast.error(`Sent to ${successCount}, ${failCount} failed. Check the console for details.`);
  }
};




  const handleFollowUp = async (recruiter, followUpMessage) => {
    setSendingEmail(true);
    try {
      // Get the original email logs to find the thread ID
      const logs = emailLogs[recruiter._id] || [];
      const originalEmail = logs.find(log => log.status === 'thread_start');

      if (!originalEmail) {
        toast.error('No original email found to reply to.');
        setSendingEmail(false);
        return;
      }

      const replyThreadId = originalEmail.threadId || originalEmail.messageId || recruiter.threadId;
      if (!replyThreadId) {
        toast.error("Can't find the original Gmail thread to reply on. Try reopening this recruiter's row first.");
        setSendingEmail(false);
        return;
      }

      // Fetch the saved template — used for its subject always, and for the
      // body too when the follow-up itself doesn't override it.
      let finalHtml = followUpMessage || '';
      let baseSubject = "Application For Engineering Roles (SDE-1)";
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/template`, {
          params: { email: user?.email },
        });
        if (response.data?.success && response.data.htmlEmailTemplate) {
          baseSubject = response.data.htmlEmailTemplate.subject || baseSubject;
          if (!followUpMessage) {
            finalHtml = response.data.htmlEmailTemplate.finalHtml;
          }
        }
      } catch (fetchTemplateError) {
        console.error('Error fetching HTML template:', fetchTemplateError);
        if (!followUpMessage) {
          toast.error('Failed to fetch email template.');
          setSendingEmail(false);
          return;
        }
      }

      if (!followUpMessage) {
        // Replace variables in the template
        finalHtml = finalHtml.replace(/{{\s*recruiter\.name\s*}}/g, recruiter.name);
      }

      // Follow-ups go through the same send endpoint as the first email —
      // passing threadId makes the backend reply on the existing Gmail
      // thread and log it as a "follow_up" instead of a new thread_start.
      const formData = new FormData();
      formData.append("from", user?.email);
      formData.append("to", recruiter.email);
      formData.append("recruiterId", recruiter._id);
      formData.append("subject", `Re: ${baseSubject}`);
      formData.append("body", finalHtml);
      formData.append("threadId", replyThreadId);

      await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/send`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update recruiter status to show follow-up sent
      setRecruiters(prev =>
        prev.map(r =>
          r._id === recruiter._id ? {
            ...r,
            status: "follow_up",
            sentAt: new Date().toISOString()
          } : r
        )
      );

      toast.success('Follow-up sent!');

      // Refresh logs for this recruiter
      await fetchEmailLogs(recruiter._id);
    } catch (error) {
      console.error('Error sending follow-up email:', error);
      toast.error(error.response?.data?.message || 'Failed to send follow-up.');
    } finally {
      setTimeout(() => setSendingEmail(false), 1500);
    }
  };



  // Helper function to check if initial email has been sent
  const hasSentEmail = (recruiter) => {
    const logs = emailLogs[recruiter._id] || [];
    return logs.some(log => log.status === 'thread_start');
  };

  // Helper function to check if conversation is closed
  const isConversationClosed = (recruiter) => {
    const logs = emailLogs[recruiter._id] || [];
    return logs.some(log => log.status === 'closed');
  };

  // One friendly label per recruiter, instead of showing raw backend enum
  // values (e.g. "thread_start") straight in the UI.
  const getDisplayStatus = (recruiter) => {
    if (isConversationClosed(recruiter)) return 'Closed';
    if (recruiter.status === 'thread_start') return 'Email Sent';
    if (recruiter.status === 'Follow-up Sent' || recruiter.status === 'follow_up') return 'Follow-up Sent';
    return 'No Response';
  };

  const STATUS_FILTERS = ['all', 'No Response', 'Email Sent', 'Follow-up Sent', 'Closed'];

  // Recruiters are a shared, platform-wide directory and can run into the
  // thousands — filter + cap what's rendered at once so the table stays
  // fast and doesn't dump every row into the DOM.
  const searchTerm = recruiterSearch.trim().toLowerCase();
  const fromTime = dateFrom ? new Date(dateFrom).getTime() : null;
  const toTime = dateTo ? new Date(dateTo).getTime() + 24 * 60 * 60 * 1000 - 1 : null; // inclusive of whole "to" day

  const filteredRecruiters = recruiters.filter((r) => {
    if (searchTerm) {
      const matchesSearch = [r.name, r.email, r.company].some((field) =>
        field?.toLowerCase().includes(searchTerm)
      );
      if (!matchesSearch) return false;
    }

    if (statusFilter !== 'all' && getDisplayStatus(r) !== statusFilter) return false;

    if (fromTime || toTime) {
      const sentTime = r.sentAt ? new Date(r.sentAt).getTime() : null;
      if (!sentTime) return false;
      if (fromTime && sentTime < fromTime) return false;
      if (toTime && sentTime > toTime) return false;
    }

    return true;
  });

  const visibleRecruiters = filteredRecruiters.slice(0, visibleCount);
  const hasActiveFilters = Boolean(searchTerm || statusFilter !== 'all' || dateFrom || dateTo);

  // Bulk send only ever targets recruiters nobody has reached out to yet —
  // it reuses the single "Send Mail" flow per recipient, so a thread that's
  // already started needs the follow-up flow instead.
  const BULK_SEND_CAP = 25;
  const bulkEligible = visibleRecruiters.filter((r) => getDisplayStatus(r) === 'No Response');

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllEligible = () => {
    setSelectedIds((prev) => {
      const allSelected = bulkEligible.length > 0 && bulkEligible.every((r) => prev.has(r._id));
      if (allSelected) return new Set();
      const capped = bulkEligible.slice(0, BULK_SEND_CAP);
      if (bulkEligible.length > BULK_SEND_CAP) {
        toast(`Selected the first ${BULK_SEND_CAP} to keep bulk sends from looking like spam.`, { icon: '⚠️' });
      }
      return new Set(capped.map((r) => r._id));
    });
  };

  // Infinite scroll: reveal more already-fetched rows as the sentinel at
  // the bottom of the table comes into view, instead of a manual "Load
  // more" click.
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((c) => Math.min(c + 50, filteredRecruiters.length));
        }
      },
      { rootMargin: '300px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [filteredRecruiters.length]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Lottie animationData={loaderAnimation} loop style={{ width: 200, height: 200 }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <div className="max-w-7xl mx-auto px-6 py-10">

      {/* Email Connection Status */}
      {isConnected && user?.email ? (
        <div className="flex items-center gap-4 mb-6 bg-white border border-green-200 p-4 rounded-2xl shadow-sm">
          <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-green-50 shrink-0">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          </span>
          <div>
            <p className="text-xs font-medium text-green-700">Connected Email</p>
            <p className="text-sm font-semibold text-gray-900">{user.email}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 bg-white border border-orange-200 p-4 rounded-2xl shadow-sm">
          <div className="flex items-center gap-4">
            <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-orange-50 shrink-0">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
            </span>
            <div>
              <p className="text-sm font-semibold text-gray-900">Email not connected</p>
              <p className="text-xs text-gray-500">Connect Gmail to send and track applications from this page</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleConnectClick}
              className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-blue-600/20 hover:bg-blue-700 transition-colors"
            >
              <Mail size={16} />
              Connect Gmail
            </button>
            <button
              className="inline-flex items-center gap-2 bg-white text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
              onClick={() => navigate('/email-editor')}
            >
              <PenSquare size={16} />
              Open Email Editor
            </button>
          </div>
        </div>
      )}

      {/* Header + Toolbar */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Recruiter Email Tracker</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Reach out to recruiters, follow up, and track every reply in one place
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <button
              className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-blue-600/20 hover:bg-blue-700 transition-colors"
              onClick={() => setIsModalOpen(true)}
            >
              <UserPlus size={16} />
              Add Recruiter
            </button>
            <button
              className="inline-flex items-center gap-2 bg-white text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
              onClick={handleConnectClick}
            >
              <Mail size={16} />
              Connect New Email
            </button>
            <button
              className="inline-flex items-center gap-2 bg-white text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
              onClick={() => navigate('/email-editor')}
            >
              <PenSquare size={16} />
              Open Email Editor
            </button>
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleExcelUpload}
              className="hidden"
              id="excel-upload"
            />
            <label
              htmlFor="excel-upload"
              className="inline-flex items-center gap-2 cursor-pointer bg-white text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <Upload size={16} />
              Upload Excel
            </label>
            <button
              onClick={handleSyncSheet}
              disabled={syncingSheet}
              title="Pull the latest rows from the linked Google Sheet"
              className="inline-flex items-center gap-2 bg-white text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 disabled:opacity-60 transition-colors"
            >
              <RefreshCw size={16} className={syncingSheet ? "animate-spin" : ""} />
              {syncingSheet ? "Syncing..." : "Sync Sheet"}
            </button>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-gray-100">
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
            <FileText size={15} className="text-gray-400" />
            Resume to attach
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={(e) => setResumeFile(e.target.files[0])}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4
               file:rounded-xl file:border-0
               file:text-sm file:font-semibold
               file:bg-blue-50 file:text-blue-700
               hover:file:bg-blue-100 file:cursor-pointer"
          />
          {resumeFile && (
            <p className="text-sm text-green-600 mt-2 flex items-center gap-1.5">
              <CheckCircle2 size={14} />
              {resumeFile.name} selected
            </p>
          )}
        </div>
      </div>


      {/* Recruiter Table */}
      {recruiters.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={recruiterSearch}
              onChange={(e) => {
                setRecruiterSearch(e.target.value);
                setVisibleCount(50);
              }}
              placeholder="Search by name, email or company"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setVisibleCount(50);
            }}
            className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>
            ))}
          </select>

          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setVisibleCount(50); }}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setVisibleCount(50); }}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {hasActiveFilters && (
            <button
              onClick={() => { setRecruiterSearch(''); setStatusFilter('all'); setDateFrom(''); setDateTo(''); setVisibleCount(50); }}
              className="text-xs font-semibold text-gray-500 hover:text-gray-700"
            >
              Clear filters
            </button>
          )}

          {isAdmin && filteredRecruiters.length > 0 && (
            <button
              onClick={handleDeleteAllFiltered}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700"
            >
              <Trash2 size={13} />
              {hasActiveFilters ? `Delete all ${filteredRecruiters.length} filtered` : 'Delete all'}
            </button>
          )}

          <p className="text-xs text-gray-400 ml-auto shrink-0">
            {filteredRecruiters.length.toLocaleString()} recruiter{filteredRecruiters.length === 1 ? "" : "s"}
          </p>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4">
          <p className="text-sm text-blue-800 font-medium">
            {selectedIds.size} selected
            {bulkSending && ` — sending ${bulkProgress.done}/${bulkProgress.total}...`}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkSending}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 disabled:opacity-50"
            >
              Clear
            </button>
            {isAdmin && (
              <button
                onClick={handleDeleteSelected}
                disabled={bulkSending}
                className="inline-flex items-center gap-2 bg-red-50 text-red-600 text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-red-100 disabled:opacity-60 transition-colors"
              >
                <Trash2 size={14} />
                Delete Selected
              </button>
            )}
            <button
              onClick={handleBulkSend}
              disabled={bulkSending}
              className="inline-flex items-center gap-2 bg-blue-600 text-white text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              <Mail size={14} />
              {bulkSending ? 'Sending...' : `Send to ${selectedIds.size}`}
            </button>
          </div>
        </div>
      )}
      <div className="bg-white border border-gray-200 shadow-sm rounded-2xl overflow-hidden">
        {recruiters.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">No recruiters yet</h3>
            <p className="text-sm text-gray-500 mb-5">
              Add a recruiter or upload an Excel sheet to start tracking outreach
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-blue-600/20 hover:bg-blue-700 transition-colors"
            >
              <UserPlus size={16} />
              Add Recruiter
            </button>
          </div>
        ) : filteredRecruiters.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">No matches</h3>
            <p className="text-sm text-gray-500">No recruiters match "{recruiterSearch}"</p>
          </div>
        ) : (
        <div className="overflow-x-auto">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="pl-5 pr-2 py-3 w-10">
                <input
                  type="checkbox"
                  title="Select up to 25 for bulk send"
                  checked={bulkEligible.length > 0 && bulkEligible.every((r) => selectedIds.has(r._id))}
                  onChange={toggleSelectAllEligible}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Company</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Sent At</th>
              <th className="px-5 py-3"></th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibleRecruiters.map((r) => (
              <React.Fragment key={r._id}>
                <tr className="hover:bg-blue-50/40 transition-colors">
                  <td className="pl-5 pr-2 py-3.5">
                    {/* Non-admins can only select not-yet-contacted rows (for
                        bulk send). Admins can select any row — they also
                        use this selection to bulk-delete. */}
                    {(isAdmin || getDisplayStatus(r) === 'No Response') && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r._id)}
                        onChange={() => toggleSelect(r._id)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 shrink-0">
                        <FaUser className="text-blue-500 text-xs" />
                      </span>
                      <span className="text-sm font-medium text-gray-900">{r.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-gray-600">{r.email}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-600">{r.company || "—"}</td>
                  <td className="px-5 py-3.5">
                    {(() => {
                      const displayStatus = getDisplayStatus(r);
                      const statusStyle =
                        displayStatus === 'Email Sent'
                          ? "bg-blue-50 text-blue-600"
                          : displayStatus === 'Follow-up Sent'
                            ? "bg-orange-50 text-orange-600"
                            : displayStatus === 'Closed'
                              ? "bg-gray-100 text-gray-600"
                              : "bg-yellow-50 text-yellow-700";
                      return (
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${statusStyle}`}>
                          {displayStatus}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-gray-500">
                    {r.sentAt ? (
                      <span className="inline-flex items-center gap-1.5">
                        <FaClock className="text-gray-400" />
                        {new Date(r.sentAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      {!hasSentEmail(r) ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendMail(r);
                          }}
                          disabled={sendingEmail}
                          className={`bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors ${sendingEmail ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        >
                          Send Mail
                        </button>
                      ) : hasSentEmail(r) && !isConversationClosed(r) ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchFollowUpTemplates(r._id);
                          }}
                          disabled={sendingEmail}
                          className={`bg-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-orange-600 transition-colors ${sendingEmail ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        >
                          Take Follow-up
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">Conversation Closed</span>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteRecruiter(r)}
                          className="inline-flex items-center gap-1 bg-red-50 text-red-600 text-xs font-semibold px-2.5 py-1.5 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <Trash2 size={13} />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    {hasSentEmail(r) && (
                      <button
                        onClick={async () => {
                          await fetchEmailLogs(r._id);
                          setJourneyRecruiter(r);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700"
                      >
                        <Eye size={14} />
                        View Journey
                      </button>
                    )}
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>

        </table>
        </div>
        )}
      </div>

      {filteredRecruiters.length > visibleCount && (
        <div ref={loadMoreRef} className="flex justify-center py-6">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
            Loading more...
          </div>
        </div>
      )}
      </div>

      {/* Modal to Add Recruiter */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                    Add Recruiter
                  </Dialog.Title>
                  <div className="mt-4 space-y-3">
                    <input
                      type="text"
                      placeholder="Name"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={newRecruiter.name}
                      onChange={(e) => setNewRecruiter(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={newRecruiter.email}
                      onChange={(e) => setNewRecruiter(prev => ({ ...prev, email: e.target.value }))}
                    />
                    <input
                      type="text"
                      placeholder="Company (optional)"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={newRecruiter.company}
                      onChange={(e) => setNewRecruiter(prev => ({ ...prev, company: e.target.value }))}
                    />
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                      onClick={handleAddRecruiter}
                    >
                      Add
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Modal to Select Follow-up Template */}
      <Transition appear show={showFollowUpModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowFollowUpModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    Select a Follow-up Template
                  </Dialog.Title>
                  <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                    {followUpTemplates.length === 0 && (
                      <div className="text-gray-500 text-center py-6">
                        <div className="text-sm">No follow-up templates found.</div>
                        <div className="text-xs text-gray-400 mt-1">Please create templates in the Email Editor first.</div>
                      </div>
                    )}
                    {followUpTemplates.map((tpl, idx) => (
                      <div key={tpl.id || idx} className={`border rounded-xl p-4 cursor-pointer transition-colors ${selectedFollowUpTemplate === tpl ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                        onClick={() => setSelectedFollowUpTemplate(tpl)}>
                        <div className="font-semibold mb-2 text-sm text-gray-900">{tpl.name || `Template ${idx + 1}`}</div>
                        <div className="text-sm whitespace-pre-line text-gray-600 max-h-32 overflow-y-auto bg-white p-2.5 rounded-lg border border-gray-100">
                          {tpl.message || 'No message content'}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setShowFollowUpModal(false);
                        setFollowUpTargetId(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      disabled={!selectedFollowUpTemplate}
                     onClick={async () => {
  const recruiter = recruiters.find(r => r._id === followUpTargetId);
  if (!recruiter) {
    toast.error("Couldn't tell which recruiter this follow-up was for — please try again.");
    return;
  }

  setShowFollowUpModal(false);
  await handleFollowUp(recruiter, selectedFollowUpTemplate.message);
  setFollowUpTargetId(null);
}}

                    >
                      Send Follow-up
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Modal to View Email Journey */}
      <Transition appear show={!!journeyRecruiter} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setJourneyRecruiter(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Email Journey
                    </Dialog.Title>
                    <button
                      onClick={() => setJourneyRecruiter(null)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  {journeyRecruiter && (
                    <StatusAccordion
                      recruiter={{
                        ...journeyRecruiter,
                        threadId: journeyRecruiter.threadId || journeyRecruiter._id,
                      }}
                      logs={emailLogs[journeyRecruiter._id] || []}
                    />
                  )}

                  <div className="mt-6 flex justify-end">
                    <button
                      className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                      onClick={() => setJourneyRecruiter(null)}
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Email Sending Animation */}
      {sendingEmail && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 backdrop-blur-sm flex justify-center items-center">
          <Lottie animationData={mailSent} loop={false} style={{ width: 200, height: 200 }} />
        </div>
      )}
    </div>
  );
};

export default EmailSender;
